import { AlertSidebarDefinition } from 'app/modules/sidebar/models';
import { useGetAlert } from 'app/modules/alerts/queries/useGetAlert';
import { IconArrowRight, IconFlag, IconTag } from '@u21/tabler-icons';
import { ROUTES_MAP } from 'app/shared/utils/routes';
import { U21SideMenu } from 'app/shared/u21-ui/components/dashboard';
import {
  U21Chip,
  U21LoadError,
  U21HelpTooltip,
  U21ShowMoreList,
  U21Spacer,
  U21Subsection,
  U21NoData,
} from 'app/shared/u21-ui/components';
import { useSelector } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { ALERT_QUERY_KEYS } from 'app/modules/alerts/queries/keys';
import { AlertSource, AlertType } from 'app/modules/alerts/types';
import { SidebarDataRow } from 'app/modules/sidebar/components/utils/SidebarDataRow';
import {
  ALERT_SCORE_TOOLTIP_NO_RISK,
  ALERT_SCORE_TOOLTIP_RISK,
} from 'app/modules/alerts/constants';
import { AlertScoreLabel } from 'app/modules/investigations/components/SummaryCard/AlertScoreLabel';
import { formatDatetime } from 'app/shared/utils/date';
import { selectFormatAmount } from 'app/modules/orgSettings/selectors';
import { useGetAlertTransactions } from 'app/modules/alerts/queries/useGetAlertTransactions';
import { DEFAULT_PAGINATION_PAYLOAD } from 'app/shared/pagination/constants';
import { SidebarCommentsCollapsible } from 'app/modules/sidebar/components/SidebarCommentsCollapsible';
import { SidebarCustomData } from 'app/modules/sidebar/components/SidebarCustomData';
import { SidebarAssociatedEntities } from 'app/modules/sidebar/components/SidebarAssociatedEntities';
import { useGetAlertEntities } from 'app/modules/alerts/queries/useGetAlertEntities';
import { SidebarAssociatedTransactions } from 'app/modules/sidebar/components/SidebarAssociatedTransactions';
import { SidebarAssociatedActionEvents } from 'app/modules/sidebar/components/SidebarAssociatedActionEvents';
import { useGetAlertActionEvents } from 'app/modules/alerts/queries/useGetAlertActionEvents';
import { SidebarAssociatedInstruments } from 'app/modules/sidebar/components/SidebarAssociatedInstruments';
import { useGetAlertInstruments } from 'app/modules/alerts/queries/useGetAlertInstruments';
import { getTagDisplayName } from 'app/modules/tags/utils';
import { DispositionChip } from 'app/modules/investigations/components/DispositionChip';
import { StatusChip } from 'app/modules/investigations/components/StatusChip';
import { ArticleDeadlineChip } from 'app/modules/deadlines/components/ArticleDeadlineChip';
import { Reassign } from 'app/modules/alerts/components/SideBySide/Reassign';
import { Requeue } from 'app/modules/alerts/components/SideBySide/Requeue';
import { useGetAlertComments } from 'app/modules/alerts/queries/useGetAlertComments';
import { useFetchAlertHits } from 'app/modules/alerts/queries/useFetchAlertHits';
import { SidebarAlertUpdates } from 'app/modules/sidebar/components/SidebarAlertUpdates';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { selectAiDecisioningV0Enabled } from 'app/shared/featureFlags/selectors';
import { ColdStorageLoadingStatus } from 'app/shared/utils/fastArchHelpers';
import { AiAgentRecommendationChip } from 'app/modules/aiAgentResults/components/AiAgentRecommendationChip';
import { IconAiAgent } from 'app/shared/components/Icons/IconAiAgent';
import { useGetAIResults } from 'app/modules/aiAgentResults/queries/useGetAIResults';
import { AIMarkdown } from 'app/modules/ai/components/AIMarkdown';
import { AIHeader } from 'app/modules/ai/components/AIHeader';
import { AILoading } from 'app/modules/ai/components/AILoading';
import { FlowChip } from 'app/modules/detectionModels/components/FlowChip';
import { RuleChip } from 'app/modules/detectionModels/components/RuleChip';

interface Props {
  data: AlertSidebarDefinition['data'];
}

export const SidebarAlert = (props: Props) => {
  const {
    data: {
      alert: { id },
      hideActionButton,
      scrollTo,
    },
  } = props;

  const formatAmount = useSelector(selectFormatAmount);
  const aiDecisioningEnabled = useSelector(selectAiDecisioningV0Enabled);

  const queryClient = useQueryClient();
  const {
    alert,
    query: { isError, isLoading, refetch },
    coldStorageLoading,
  } = useGetAlert(id);

  const {
    data: { count: entitiesCount, entities } = { count: 0, entities: [] },
    isLoading: isLoadingEntities,
  } = useGetAlertEntities({
    alertId: String(id),
    ...DEFAULT_PAGINATION_PAYLOAD,
    sort_column: 'created_at',
    sort_direction: 'descending',
  });
  const loadingEntities =
    isLoadingEntities ||
    coldStorageLoading.entity === ColdStorageLoadingStatus.LOADING;

  const { data: transactions = [], isLoading: isLoadingTransactions } =
    useGetAlertTransactions(id, {
      ...DEFAULT_PAGINATION_PAYLOAD,
      sort_column: 'event_time',
      sort_direction: 'descending',
    });
  const loadingTransactions =
    isLoadingTransactions ||
    coldStorageLoading.txn_event === ColdStorageLoadingStatus.LOADING;

  const {
    data: { count: actionEventsCount, events: actionEvents } = {
      count: 0,
      events: [],
    },
    isLoading: isLoadingActionEvents,
  } = useGetAlertActionEvents(id, {
    ...DEFAULT_PAGINATION_PAYLOAD,
    sort_column: 'event_time',
    sort_direction: 'descending',
  });
  const loadingActionEvents =
    isLoadingActionEvents ||
    coldStorageLoading.action_event === ColdStorageLoadingStatus.LOADING;

  const {
    data: { count: instrumentsCount, instruments } = {
      count: 0,
      instruments: [],
    },
    isLoading: isLoadingInstruments,
  } = useGetAlertInstruments(id, {
    ...DEFAULT_PAGINATION_PAYLOAD,
    sort_column: 'created_at',
    sort_direction: 'descending',
  });
  const loadingInstruments =
    isLoadingInstruments ||
    coldStorageLoading.instrument === ColdStorageLoadingStatus.LOADING;

  const { data: alertHits = { hits: [], count: 0 } } = useFetchAlertHits(
    id,
    DEFAULT_PAGINATION_PAYLOAD.offset,
    10,
  );

  const invalidateAlertListCache = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ALERT_QUERY_KEYS.getAllAlerts(),
    });
  }, [queryClient]);

  const refetchAlert = () => {
    queryClient.invalidateQueries({
      queryKey: ALERT_QUERY_KEYS.getAlert(id),
    });
  };

  const { comments: alertComments } = useGetAlertComments(id, {
    reverse: false,
  });

  const showAlertUpdates = useMemo(
    () => alert && alert.rules.length > 0,
    [alert],
  );

  const ruleValidationReadOnly = useMemo(
    () => alert?.status === 'RULE_VALIDATION',
    [alert],
  );

  const {
    data: aiResult,
    isError: isAIResultError,
    isLoading: isAIResultLoading,
    refetch: refetchAIResults,
  } = useGetAIResults({
    relation_id: String(id),
    relation_type: 'ALERT',
  });

  const aiRecommendationRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (
      isLoading ||
      scrollTo !== 'AI_RECOMMENDATION' ||
      !aiRecommendationRef.current
    ) {
      return;
    }
    if (aiResult?.decision_agent_recommendation) {
      aiRecommendationRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [isLoading, scrollTo, aiResult]);

  return (
    <U21SideMenu
      actionButtonProps={
        hideActionButton
          ? undefined
          : {
              children: 'Go to Alert Page',
              endIcon: <IconArrowRight />,
              to: ROUTES_MAP.alertsId.path.replace(':id', String(id)),
            }
      }
      loading={isLoading}
      noPadding
      title="Alert Details"
    >
      {(() => {
        // loading handled by U21SideMenu
        if (isLoading) {
          return null;
        }
        if (isError) {
          return <U21LoadError label="alert" onTryAgain={() => refetch()} />;
        }

        if (!alert) {
          return null;
        }

        return (
          <U21Spacer spacing={0.5}>
            <U21Subsection
              disableTitleCase
              shadedHeader
              title={alert.title}
              titleIcon={<IconFlag />}
            >
              <U21Spacer>
                <SidebarDataRow
                  label="Description"
                  showEmptyFields
                  valueProps={{ breakWord: true, ellipsis: false }}
                >
                  {alert.description}
                </SidebarDataRow>
                {!ruleValidationReadOnly && (
                  <>
                    <SidebarDataRow label="Assigned to" showEmptyFields>
                      <Reassign id={id} onSuccess={invalidateAlertListCache} />
                    </SidebarDataRow>
                    <SidebarDataRow label="Queue" showEmptyFields>
                      <Requeue id={id} onSuccess={invalidateAlertListCache} />
                    </SidebarDataRow>
                  </>
                )}
                <SidebarDataRow label="Status" showEmptyFields>
                  <StatusChip status={alert.status} />
                </SidebarDataRow>
                <SidebarDataRow label="Deadline" showEmptyFields>
                  {alert.status === 'OPEN' && (
                    <ArticleDeadlineChip
                      articleId={id}
                      deadline={alert.deadline}
                      deadlineConfig={alert.deadline_config}
                      onEditSuccess={() => {
                        refetchAlert();
                        invalidateAlertListCache();
                      }}
                    />
                  )}
                </SidebarDataRow>
                {alert.alert_score && (
                  <SidebarDataRow label="Alert score" showEmptyFields>
                    <U21Spacer horizontal>
                      <AlertScoreLabel alertScore={alert.alert_score} />
                      <U21HelpTooltip
                        help={
                          alert.alert_score.risk
                            ? ALERT_SCORE_TOOLTIP_RISK
                            : ALERT_SCORE_TOOLTIP_NO_RISK
                        }
                      />
                    </U21Spacer>
                  </SidebarDataRow>
                )}
                <SidebarDataRow label="Disposition" showEmptyFields>
                  {alert.disposition && (
                    <DispositionChip disposition={alert.disposition} />
                  )}
                </SidebarDataRow>
                <SidebarDataRow
                  label="Disposition notes"
                  showEmptyFields
                  valueProps={{ breakWord: true, ellipsis: false }}
                >
                  {alert.disposition_notes}
                </SidebarDataRow>
                <SidebarDataRow label="Flagged at" showEmptyFields>
                  {formatDatetime(alert.created_at)}
                </SidebarDataRow>
                {transactions.length > 0 && (
                  <SidebarDataRow label="Flagged amount" showEmptyFields>
                    {formatAmount({
                      amount: alert.amount_volume,
                      currencyCodeProps: alert.currency,
                    })}
                  </SidebarDataRow>
                )}
                <SidebarDataRow label="Rules triggered" showEmptyFields>
                  {alert.rules.length > 0 && (
                    <U21ShowMoreList
                      displayFunc={(i) => <RuleChip rule={i} />}
                      value={alert.rules}
                    />
                  )}
                </SidebarDataRow>
                <SidebarDataRow label="Flow triggered" showEmptyFields>
                  {alert.rule_flows && alert.rule_flows.length > 0 && (
                    <U21ShowMoreList
                      displayFunc={(flow) => (
                        <FlowChip key={flow.id} flow={flow} />
                      )}
                      value={alert.rule_flows}
                    />
                  )}
                </SidebarDataRow>
                <SidebarDataRow label="Tags" showEmptyFields>
                  {alert.tags.length > 0 && (
                    <U21ShowMoreList
                      displayFunc={(i) => (
                        <U21Chip key={i.id} icon={<IconTag />}>
                          {getTagDisplayName(i)}
                        </U21Chip>
                      )}
                      value={alert.tags}
                    />
                  )}
                </SidebarDataRow>
              </U21Spacer>
            </U21Subsection>
            {aiDecisioningEnabled &&
              aiResult?.decision_agent_recommendation && (
                <U21Subsection
                  ref={aiRecommendationRef}
                  collapsible
                  collapsed={scrollTo !== 'AI_RECOMMENDATION'}
                  shadedHeader
                  title={
                    <U21Spacer horizontal useFlexGap>
                      <AIHeader disableNew />
                      <AiAgentRecommendationChip
                        id={String(id)}
                        type="ALERT"
                        value={aiResult.decision_agent_recommendation}
                      />
                    </U21Spacer>
                  }
                  titleIcon={<IconAiAgent />}
                >
                  {(() => {
                    if (isAIResultLoading || aiResult?.is_reviewing) {
                      return <AILoading />;
                    }
                    if (isAIResultError) {
                      return (
                        <U21LoadError
                          label="AI agent results"
                          onTryAgain={() => refetchAIResults()}
                        />
                      );
                    }
                    if (!aiResult?.decision_agent_recommendation_reasoning) {
                      return (
                        <U21NoData>
                          No recommendation reasoning found.
                        </U21NoData>
                      );
                    }
                    return (
                      <AIMarkdown>
                        {aiResult.decision_agent_recommendation_reasoning}
                      </AIMarkdown>
                    );
                  })()}
                </U21Subsection>
              )}
            {(alert.alert_type === AlertType.MANUAL ||
              alert.object_source === AlertSource.EXTERNAL) && (
              <SidebarCustomData data={alert.formatted_data} />
            )}
            <SidebarAssociatedEntities
              count={entitiesCount}
              entities={entities}
              loading={loadingEntities}
              coldStorageLoadingStatus={coldStorageLoading.entity}
            />
            <SidebarAssociatedInstruments
              count={instrumentsCount}
              instruments={instruments}
              loading={loadingInstruments}
              coldStorageLoadingStatus={coldStorageLoading.instrument}
            />
            <SidebarAssociatedTransactions
              count={transactions.length}
              transactions={transactions}
              loading={loadingTransactions}
              coldStorageLoadingStatus={coldStorageLoading.txn_event}
            />
            <SidebarAssociatedActionEvents
              count={actionEventsCount}
              actionEvents={actionEvents}
              loading={loadingActionEvents}
              coldStorageLoadingStatus={coldStorageLoading.action_event}
            />
            {showAlertUpdates && (
              <SidebarAlertUpdates
                count={alertHits.count}
                alertUpdates={alertHits}
                rule={alert.rules[0]}
                alertId={id}
              />
            )}
            <SidebarCommentsCollapsible comments={alertComments} />
          </U21Spacer>
        );
      })()}
    </U21SideMenu>
  );
};
